/**
 * endOfLiveQuiz — le quiz de fin de séance : celui d'une sortie du live, demandé une fois.
 *
 * Le serveur le tire à la première demande d'une sortie (`POST live-sessions/{id}/end-of-live-quiz`)
 * et rend le même tant que l'élève n'est pas revenu dans la salle ; `null` quand il n'y en a pas. La
 * sortie le demande (la clôture par le tuteur, dont le message annonce sa durée ; le départ avant la
 * fin) et l'écran du quiz, poussé juste après, relit cette demande-là : la dernière est gardée ici,
 * le temps de passer de l'un à l'autre. Un échec vaut « pas de quiz » : la suite vient sans lui ; une
 * demande qui pend aussi, au-delà de `END_OF_LIVE_QUIZ_WAIT_MS` (aucune requête de l'app n'a de délai).
 *
 * Une réponse part sans que le jeu l'attende, comme celles du défi éclair (`answerChallenge`), mais
 * l'écran en apprend l'échec : le pétale n'est gagné que si toutes les réponses sont arrivées.
 */
import { liveService } from "../../../data/services/liveService"
import { type ApiChallengeQuestion } from "../../activity/correction/correctionChallenge"
import { apiCall } from "../../shared/apiCall"
import { type EngineTelemetry } from "../../shared/telemetry"

/** Le quiz tel que le serveur le sert. */
export type ApiEndOfLiveQuiz = {
    id: number
    /** Les réponses déjà données à cette tentative (l'écran rouvert dans la même sortie). */
    answered?: number | null
    right?: number | null
    /** Ses questions que l'élève a ratées et jamais réussies. */
    missed?: number | null
    minutes?: number | null
    /** Le pétale encore à gagner : 0 quand un quiz de ce live l'a déjà rapporté. */
    petals?: number | null
    /** La série, dans son ordre, les réponses déjà mélangées. */
    questions?: ApiChallengeQuestion[] | null
}

type Dispatch = (action: never) => unknown

/** Au-delà, une demande qui pend vaut « pas de quiz » : l'élève ne reste pas sur « Sam prépare ton quiz… ». */
export const END_OF_LIVE_QUIZ_WAIT_MS = 10_000

let lastExit: { liveSessionId: number; quiz: Promise<ApiEndOfLiveQuiz | null> } | null = null

/** La sortie du live : son quiz, demandé à neuf. */
export function requestEndOfLiveQuiz(
    dispatch: Dispatch,
    liveSessionId: number,
    telemetry: EngineTelemetry
): Promise<ApiEndOfLiveQuiz | null> {
    const request = apiCall<ApiEndOfLiveQuiz | null>(dispatch, liveService.getEndOfLiveQuiz, {
        routeParams: { id: liveSessionId },
    })
        .then((data) => data ?? null)
        .catch((cause: unknown) => {
            telemetry.error("end_of_live_quiz", cause)
            return null
        })
    let timer: ReturnType<typeof setTimeout> | undefined
    const late = new Promise<null>((resolve) => {
        timer = setTimeout(() => {
            telemetry.error("end_of_live_quiz", new Error(`pas de réponse en ${END_OF_LIVE_QUIZ_WAIT_MS} ms`))
            resolve(null)
        }, END_OF_LIVE_QUIZ_WAIT_MS)
    })
    const quiz = Promise.race([request, late]).finally(() => clearTimeout(timer))
    lastExit = { liveSessionId, quiz }
    return quiz
}

/** Le quiz de la dernière sortie de cette séance, ou demandé s'il ne l'a pas été (l'écran rouvert à froid). */
export function endOfLiveQuizOf(
    dispatch: Dispatch,
    liveSessionId: number,
    telemetry: EngineTelemetry
): Promise<ApiEndOfLiveQuiz | null> {
    return lastExit?.liveSessionId === liveSessionId
        ? lastExit.quiz
        : requestEndOfLiveQuiz(dispatch, liveSessionId, telemetry)
}

/** Les tests repartent d'aucune sortie. */
export function resetEndOfLiveQuiz(): void {
    lastExit = null
}

/** Une réponse : le texte simple de la réponse touchée, que le serveur juge. Rejetée si elle n'est pas arrivée. */
export function answerEndOfLiveQuiz(quizId: number, question: ApiChallengeQuestion, index: number): Promise<unknown> {
    return liveService.answerEndOfLiveQuiz(
        { quizId },
        { quiz_question_id: question.id, answer: question.choices?.[index] }
    ) as Promise<unknown>
}
